import { CountdownButton } from '@/components/common/count-down-button';
import { HeadPhoneGuideModal } from '@/components/common/head-phone-guide-modal';
import { HeadPhoneStopModal } from '@/components/common/head-phone-stop-modal';
import { SpeakerCloseModal } from '@/components/common/speaker-close-modal';
import { UsedTTSModal } from '@/components/common/used-tts-modal';
import { ZoomButtons } from '@/components/common/zoom-buttons';
import { HelpPopup } from '@/components/feature/home';
import { BuildingMenuType, UsbMediaConnectStatus, WPFMessageType } from '@/enums';
import { useAccessibilityTTS, useFocusTrap, useInteractiveElementsWithTTS, useKioskResetService, useRegionNavigation, useTickSoundInteraction } from '@/hooks';
import useBaseSize from '@/hooks/useBaseSize';
import { useDisablePinchZoom } from '@/hooks/useDisablePinchZoom';
import { useKioskNavigation } from '@/hooks/useKioskNavigation';
import { useOrientation } from '@/hooks/useOrientation';
import { FooterLayout } from '@/layouts/footer/FooterLayout';
import { GNBLayout } from '@/layouts/gnb/GNBLayout';
import { PATHS } from '@/router/paths';
import { CONFIG_CONSTANTS, useConfigStore, useModalStackStore, useHomeStore, type SystemConfig, type languageType } from '@/stores';
import { buildMenuFromBuildingMenus, getRequestStaffHelpFromBuildingMenus } from '@/stores/configStore';
import type { GNBItemType } from '@/types';
import { resetKioskSessionToHomeScreensaver } from '@/utils/kioskSessionReset';
import { willBackGoToHome } from '@/utils/navigationHistory';
import { postAudioDeviceChangeMessage } from '@/utils/post-message';
import { convertNumberToKorean } from '@/utils/number';
import { getHSize, getSize } from '@/utils/size';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styles from './MainLayout.module.scss';
import { useCountdown } from '@/hooks/useCountDown';
import { zoomLevelToScale } from '@/constants/zoom';
import { resetService } from '@/services/resetService';
import { ChangedToHeadphoneModal } from '@/components/common/changed-to-headphone-modal';
import { DEFAULT_MENUS, KEY_CODE_SIHEUNG, MENU_CUSTOM } from '@/constants/app';
import { CustomMenuListCode } from '@/constants/custom-menu-list-code';
import { useGetSvgDataQuery } from '@/services/queries/svg';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useCurrentCustomMenu } from '@/hooks/custom-menu';

interface MainLayoutProps {
    children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const { t } = useTranslation();
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();

    const { setMenuLayout, triggerFocusContent } = useKioskNavigation();
    const { isHorizontal } = useOrientation();
    const navigate = useNavigate();
    const location = useLocation();
    const { trapFocus, releaseFocus } = useFocusTrap();
    const layoutRef = useRef<HTMLDivElement>(null);
    const ui = useConfigStore((state) => state.config?.ui);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const zoomIn = useConfigStore((state) => state.config?.ui?.zoomIn);
    const zoomLevel = useConfigStore((state) => state.config?.ui?.zoomLevel);
    const contrast = useConfigStore((state) => state.config?.ui?.contrast);
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const menu = useConfigStore((state) => state.config?.ui?.menu);
    const tts = useConfigStore((state) => state.config?.tts);
    const volume = useConfigStore((state) => state.config?.ui?.volume);
    const buildingMenus = useConfigStore((state) => state.config?.server?.buildingMenus);
    const barrierFree = useConfigStore((state) => state.config?.ui?.barrierFree);
    const language = useConfigStore((state) => state.config?.ui?.language);
    const storeLangs = useConfigStore((state) => state.config?.server?.store?.storeLangs);
    const isScreenSaverFullscreen = useConfigStore((s) => s.config?.ui?.isScreenSaverFullscreen);
    const building = useConfigStore((state) => state.config.server?.building);
    const screensaverGuideEnabled = building?.screensaverGuideEnabled?.[language];
    const isHomeMenuVisible = useHomeStore((s) => s.isHomeMenuVisible);
    const isHelpPopupOpen = useHomeStore((s) => s.isHelpPopupOpen);
    const setHomeMenuVisible = useHomeStore((s) => s.setHomeMenuVisible);
    const resetHomeMenu = useHomeStore((s) => s.resetHomeMenu);
    const setHelpPopupOpen = useHomeStore((s) => s.setHelpPopupOpen);

    const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode) ?? '';
    const isUseKeypadSiheung = KEY_CODE_SIHEUNG.includes(storeCode);

    const hasUsedRef = useRef(false);
    const personDetectedRef = useRef(false);
    const repeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
    /** After Ctrl+2 on home screensaver: focus HomeDetailOverlay once TTS modal closes */
    const shouldFocusHomeDetailAfterModalRef = useRef(false);
    /** UsedTTSModal opened via Ctrl+2, i (simulated headphone connect) */
    const usedTTSModalOpenedByCtrl2Ref = useRef(false);
    const { playTick } = useTickSoundInteraction();
    const { menuCode } = useCurrentCustomMenu(gnbMenuId);

    // Language check variables
    const languageCheck = {
        isEnglish: language === 'en',
        isKorean: language === 'ko',
    };

    const { speak, speakAnnouncement, speakElement } = useAccessibilityTTS();
    const [focusTrapDisabled, setFocusTrapDisabled] = useState(false);
    const { baseWidth, baseHeight } = useBaseSize();
    const [isZoomButtonsOpen, setIsZoomButtonsOpen] = useState(false);
    const [isShowHeadPhoneGuide, setIsShowHeadPhoneGuide] = useState(false);
    const [isShowUsedTTSModal, setIsShowUsedTTSModal] = useState(false);
    const [isShowHeadPhoneStop, setIsShowHeadPhoneStop] = useState(false);
    const [isShowSpeakerClose, setIsShowSpeakerClose] = useState(false);
    const [isBarrierFreeUsed, setIsBarrierFreeUsed] = useState(false);
    const [isShowChangedToHeadphoneModal, setIsShowChangedToHeadphoneModal] = useState(false);
    // Function to close FooterLayout modals
    const [footerCloseModalFunction, setFooterCloseModalFunction] = useState<(() => void) | undefined>(undefined);

    // Use the combined hook for interactive elements with TTS
    useInteractiveElementsWithTTS();

    // Initialize region navigation
    useRegionNavigation({ speak, isHorizontal });

    // Disable pinch-to-zoom gestures
    useDisablePinchZoom();

    const { startCountdown, remaining, clearCountdown } = useCountdown({
        duration: 30,
        onComplete: () => {
            setIsShowHeadPhoneStop(false);
            const currentLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
            // Build menu using buildMenuFromBuildingMenus
            const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, currentLanguage, currentLanguage);
            updateConfig({
                ui: {
                    ...ui,
                    language: currentLanguage,
                    menu,
                    requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, currentLanguage),
                    zoomIn: false,
                    zoomClosed: true,
                    contrast: false,
                    volume: 3,
                    screenMode: 'fullscreen',
                },
                tts: {
                    ...tts,
                    speaker: 'external',
                    voice: false,
                },
            });
            setIsZoomButtonsOpen(false);
            resetHomeMenu();
            navigate(PATHS.HOME.INDEX);
        },
    });

    const isHomePage = location.pathname === PATHS.HOME.INDEX;

    useEffect(() => {
        if (!isHomePage) {
            resetHomeMenu();
        }
    }, [isHomePage, resetHomeMenu]);

    const isEventPage = useMemo(() => {
        return menuCode === CustomMenuListCode.EVENT;
    }, [menuCode]);

    // When headphone is connected, hide countdown button
    const shouldHideCountdownButton = useCallback(() => {
        return tts?.voice === true && tts?.speaker === 'headphone';
    }, [tts]);

    const handleConfigUpdate = useCallback(
        (updates: Partial<SystemConfig['ui']>) => {
            updateConfig({ ui: { ...ui, ...updates } });
        },
        [updateConfig, ui]
    );

    const handleZoomButtonsOpen = useCallback((isOpen: boolean) => {
        setIsZoomButtonsOpen(isOpen);
    }, []);

    useKioskResetService({
        isEnabled: isHomePage,
        onZoomButtonsClose: handleZoomButtonsOpen,
    });

    const handleZoomClose = useCallback(() => {
        setIsZoomButtonsOpen(false);
        handleConfigUpdate({ zoomIn: false, zoomClosed: true, zoomLevel: 1 });
        setTimeout(() => {
            document.body.style.overflow = 'hidden';
            setTimeout(() => {
                document.body.style.overflow = '';
            }, 10);
        }, 10);
    }, [handleConfigUpdate]);

    useEffect(() => {
        if (!zoomIn) {
            setIsZoomButtonsOpen(false);
        }
    }, [zoomIn]);

    const handleZoom = useCallback(
        (zoom: number) => {
            handleConfigUpdate({ zoomIn: true, zoomClosed: false, zoomLevel: zoom });
        },
        [handleConfigUpdate]
    );

    const gnbItems = useMemo<GNBItemType[]>(() => {
        const gnb = menu.flatMap((item) => {
            if (item?.disabled) return [];

            if (isScreenSaverFullscreen && item.type === BuildingMenuType.HOME) return [];

            if (item.type === BuildingMenuType.CUSTOM_MENU) {
                const menuCustom = MENU_CUSTOM.find((d) => d.type === item.customMenuType) ?? ({} as { route?: string; type?: string });
                const baseRoute = menuCustom.route;
                return {
                    ...menuCustom,
                    ...item,
                    label: typeof item?.label === 'string' ? item?.label : (item?.label?.line1 ?? ''),
                    ariaLabel: typeof item?.ariaLabel === 'string' ? item?.ariaLabel : (item?.ariaLabel?.line1 ?? null),
                    route: baseRoute && item.id ? `${baseRoute}/${item.id}` : baseRoute,
                };
            }

            const defaultItem = DEFAULT_MENUS.find((d) => d.type === item.type) ?? {};
            return { ...defaultItem, ...item };
        });

        return gnb;
    }, [menu, t, isScreenSaverFullscreen]);

    const { data: gnbItemsWithSvg } = useGetSvgDataQuery(gnbItems);

    useEffect(() => {
        setMenuLayout((prev) => ({ ...prev, gnb: gnbItems }));
    }, [gnbItems, setMenuLayout]);

    useEffect(() => {
        const handleDisableFocusTrap = () => setFocusTrapDisabled(true);
        const handleEnableFocusTrap = () => setFocusTrapDisabled(false);

        window.addEventListener('main-focus-trap-disable', handleDisableFocusTrap);
        window.addEventListener('main-focus-trap-enable', handleEnableFocusTrap);
        return () => {
            window.removeEventListener('main-focus-trap-disable', handleDisableFocusTrap);
            window.removeEventListener('main-focus-trap-enable', handleEnableFocusTrap);
        };
    }, []);

    useEffect(() => {
        if (layoutRef.current && !focusTrapDisabled) {
            trapFocus(layoutRef.current);
        } else if (focusTrapDisabled) {
            releaseFocus();
        }
        return () => {
            releaseFocus();
        };
    }, [trapFocus, releaseFocus, focusTrapDisabled]);

    const clearAllTimers = useCallback(() => {
        if (repeatIntervalRef.current) {
            clearInterval(repeatIntervalRef.current);
            repeatIntervalRef.current = null;
        }
    }, []);

    const startRepeatAnnouncement = useCallback(() => {
        if (repeatIntervalRef.current) {
            clearInterval(repeatIntervalRef.current);
        }

        repeatIntervalRef.current = setInterval(() => {
            if (personDetectedRef.current && !hasUsedRef.current) {
                speakAnnouncement(t('accessibility.tts.announcement_use_headphone'));
            }
        }, 60000); // 1 minute
    }, [speakAnnouncement, t]);

    const handleToggleTTS = useCallback(() => {
        if (isShowHeadPhoneGuide || isShowUsedTTSModal || isShowHeadPhoneStop || isShowSpeakerClose) {
            return;
        }

        if (tts?.voice && tts?.speaker === 'external') {
            setIsShowSpeakerClose(true);
            setIsShowHeadPhoneGuide(false);
            setIsShowUsedTTSModal(false);
            setIsShowChangedToHeadphoneModal(false);
            footerCloseModalFunction?.();
        } else if (tts?.voice) {
            setIsShowHeadPhoneGuide(true);
            setIsShowSpeakerClose(false);
            setIsShowUsedTTSModal(false);
            setIsShowChangedToHeadphoneModal(false);
            footerCloseModalFunction?.();
        } else {
            usedTTSModalOpenedByCtrl2Ref.current = false;
            setIsShowUsedTTSModal(true);
            setIsShowHeadPhoneGuide(false);
            setIsShowSpeakerClose(false);
            setIsShowChangedToHeadphoneModal(false);
            footerCloseModalFunction?.();
            handleBarrierFreeUsed();
            updateConfig({
                tts: {
                    ...tts,
                    voice: true,
                },
            });
        }
    }, [
        tts,
        updateConfig,
        setIsShowSpeakerClose,
        setIsShowHeadPhoneGuide,
        setIsShowUsedTTSModal,
        isShowHeadPhoneGuide,
        isShowUsedTTSModal,
        isShowHeadPhoneStop,
        isShowSpeakerClose,
    ]);

    const handleSwitchToHeadphone = useCallback(() => {
        usedTTSModalOpenedByCtrl2Ref.current = false;
        setIsShowUsedTTSModal(true);
        setIsShowHeadPhoneGuide(false);
        setIsShowSpeakerClose(false);
        setIsShowChangedToHeadphoneModal(false);
        footerCloseModalFunction?.();

        updateConfig({
            tts: {
                ...tts,
                speaker: 'headphone',
                voice: true,
            },
        });
        postAudioDeviceChangeMessage(true);
    }, [tts, updateConfig, footerCloseModalFunction]);

    const handleSwitchToExternal = useCallback(() => {
        setIsShowHeadPhoneStop(true);
        setIsShowUsedTTSModal(false);
        setIsShowHeadPhoneGuide(false);
        setIsShowSpeakerClose(false);
        setIsShowChangedToHeadphoneModal(false);
        footerCloseModalFunction?.();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: true,
            },
        });
        postAudioDeviceChangeMessage(false);
    }, [tts, updateConfig, footerCloseModalFunction]);

    const handleAnnounce = useCallback(() => {
        if (!isHomePage || isBarrierFreeUsed) return;

        personDetectedRef.current = true;
        hasUsedRef.current = false;
        speakAnnouncement(t('accessibility.tts.announcement_use_headphone'));
        startRepeatAnnouncement();
    }, [speakAnnouncement, t, startRepeatAnnouncement, isHomePage, isBarrierFreeUsed]);

    const handleAnnounceRepeat = useCallback(() => {
        if (!personDetectedRef.current && !hasUsedRef.current) {
            handleAnnounce();
        }
    }, [handleAnnounce]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        if (isHomePage && personDetectedRef.current && !isBarrierFreeUsed) {
            timeoutId = setTimeout(() => {
                handleAnnounce();
            }, 60000); // 1 minute
        } else {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [handleAnnounce, isHomePage, isBarrierFreeUsed]);

    const handleAnnounceStop = useCallback(() => {
        clearAllTimers();
        personDetectedRef.current = false;
        hasUsedRef.current = false;
    }, [clearAllTimers]);

    const handleBackToPrevious = useCallback(() => {
        if (useModalStackStore.getState().blocksGlobalKeypad()) {
            return;
        }

        if (willBackGoToHome() && !shouldHideCountdownButton()) {
            footerCloseModalFunction?.();
            setIsZoomButtonsOpen(false);
            resetKioskSessionToHomeScreensaver(navigate);
            setIsBarrierFreeUsed(false);
            return;
        }

        navigate(-1);
    }, [navigate, footerCloseModalFunction, shouldHideCountdownButton]);

    const handleNavigateHome = useCallback(() => {
        if (useModalStackStore.getState().blocksGlobalKeypad()) {
            return;
        }
        if (location.pathname === PATHS.HOME.INDEX) {
            setHomeMenuVisible(false);
            return;
        }
        footerCloseModalFunction?.();
        navigate(PATHS.HOME.INDEX);
    }, [navigate, location.pathname, footerCloseModalFunction]);

    const handleHelpRequest = useCallback(() => {
        setHelpPopupOpen(true);
        TrackingService.onHomePopupOpen();
    }, [setHelpPopupOpen]);

    const handleCloseHelpModal = useCallback(() => {
        setHelpPopupOpen(false);
        TrackingService.onHomePopupClose();
    }, [setHelpPopupOpen]);

    const focusHomeDetailOverlay = useCallback(() => {
        if (!isHomePage || !isScreenSaverFullscreen) return;

        setHomeMenuVisible(true);

        window.setTimeout(() => {
            const el = document.querySelector('[data-home-detail-focus]') as HTMLElement | null;
            el?.focus();
        }, 150);
    }, [isHomePage, isScreenSaverFullscreen, setHomeMenuVisible]);

    const focusHomeDetailAfterModalIfNeeded = useCallback(() => {
        if (!shouldFocusHomeDetailAfterModalRef.current) return;
        shouldFocusHomeDetailAfterModalRef.current = false;
        focusHomeDetailOverlay();
    }, [focusHomeDetailOverlay]);

    const handleBarrierFreeUsed = useCallback(() => {
        setIsBarrierFreeUsed(true);
    }, []);

    const handleUsbMediaChange = useCallback(
        (message: { type: WPFMessageType; action: string }) => {
            if (message?.action === UsbMediaConnectStatus.Connected) {
                if (isHomePage && isScreenSaverFullscreen) {
                    shouldFocusHomeDetailAfterModalRef.current = true;
                    setHomeMenuVisible(true);
                }

                console.log(tts.voice, 'tts.voice');

                setTimeout(() => {
                    postAudioDeviceChangeMessage(true); 
                    updateConfig({
                        tts: {
                            ...tts,
                            speaker: 'headphone',
                            voice: true,
                        },
                    });
                    if (!tts.voice) {
                        setIsShowUsedTTSModal(true);
                        setIsShowHeadPhoneGuide(false);
                        setIsShowChangedToHeadphoneModal(false);
                        setIsShowHeadPhoneStop(false);
                        setIsShowSpeakerClose(false);
                        footerCloseModalFunction?.();
                    } else {
                        usedTTSModalOpenedByCtrl2Ref.current = false;
                        setIsShowChangedToHeadphoneModal(true);
                        setIsShowUsedTTSModal(false);
                        setIsShowHeadPhoneGuide(false);
                        setIsShowHeadPhoneStop(false);
                        setIsShowSpeakerClose(false);
                        footerCloseModalFunction?.();
                    }
                }, 500);
            }

            if (message?.action === UsbMediaConnectStatus.Disconnected) {
                if (tts.voice) {
                    setIsShowHeadPhoneStop(true);
                    setIsShowUsedTTSModal(false);
                    setIsShowHeadPhoneGuide(false);
                    setIsShowChangedToHeadphoneModal(false);
                    setIsShowSpeakerClose(false);
                    footerCloseModalFunction?.();
                    startCountdown();
                }
            }
        },
        [tts, startCountdown, updateConfig, footerCloseModalFunction, isHomePage, isScreenSaverFullscreen, setHomeMenuVisible]
    );

    useEffect(() => {
        const homeFocusNavKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'];

        const handleKeyDown = (e: KeyboardEvent) => {
            if (!barrierFree) return;

            const focusedEl = document.activeElement as HTMLElement | null;
            const isInteractiveActivation = focusedEl?.closest('[data-interactive], button, [role="button"]') && (e.key === 'Enter' || e.key === ' ');
            const isRealPopupOpen = useModalStackStore.getState().modalStack.length > 0 || !!document.querySelector('[role="dialog"], [role="alertdialog"]');
            const isScreenSaverHomeMenu = isHomePage && isScreenSaverFullscreen && isHomeMenuVisible;

            if (isScreenSaverHomeMenu && !isRealPopupOpen) {
                if (isInteractiveActivation) return;

                if (homeFocusNavKeys.includes(e.key) && tts?.voice) {
                    return;
                }
            }

            const excludedKeys = ['F13', 'F14', 'F15'];
            if (!excludedKeys.includes(e.key)) {
                hasUsedRef.current = true;
            }
            const homeExcludedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'];

            if (isInteractiveActivation) {
                return;
            }

            if (isHomePage && !['h', 'i', 'o'].includes(e.key) && !(e.ctrlKey && ['1', '2'].includes(e.key))) {
                if (isScreenSaverFullscreen && !isHomeMenuVisible) {
                    if (!isRealPopupOpen) playTick('ting');
                    focusHomeDetailOverlay();
                    return;
                }
                if (homeExcludedKeys.includes(e.key) && isHomeMenuVisible) return;
                if (isRealPopupOpen) return;
                return;
            }

            if (e.ctrlKey) {
                if (e.key === '2') {
                    usedTTSModalOpenedByCtrl2Ref.current = true;
                    if (isHomePage && isScreenSaverFullscreen) {
                        shouldFocusHomeDetailAfterModalRef.current = true;
                        setHomeMenuVisible(true);
                    }
                    handleUsbMediaChange({ type: WPFMessageType.HeadphoneInOut, action: UsbMediaConnectStatus.Connected });
                    return;
                }
                if (e.key === '1') {
                    handleUsbMediaChange({ type: WPFMessageType.HeadphoneInOut, action: UsbMediaConnectStatus.Disconnected });
                    return;
                }
            }

            switch (e.key) {
                case 'h':
                    if (isUseKeypadSiheung) {
                        e.preventDefault();
                        playTick('ting');
                        handleNavigateHome();
                        break;
                    }
                    if (isScreenSaverFullscreen && screensaverGuideEnabled) {
                        handleHelpRequest();
                    }
                    break;
                case '*':
                    if (isUseKeypadSiheung) {
                        window.dispatchEvent(new CustomEvent('keypad-delete-char'));
                        break;
                    }
                    handleToggleTTS();
                    break;
                case '#':
                    if (isUseKeypadSiheung) {
                        window.dispatchEvent(new CustomEvent('keypad-clear'));
                        break;
                    }
                    if (tts?.voice) {
                        const focusedEl = document.activeElement as HTMLElement;
                        if (focusedEl && focusedEl !== document.body) {
                            speakElement(focusedEl);
                        }
                    }
                    break;
                case 'i':
                    usedTTSModalOpenedByCtrl2Ref.current = true;
                    if (isHomePage && isScreenSaverFullscreen) {
                        shouldFocusHomeDetailAfterModalRef.current = true;
                        setHomeMenuVisible(true);
                    }
                    handleUsbMediaChange({ type: WPFMessageType.HeadphoneInOut, action: UsbMediaConnectStatus.Connected });
                    break;
                case 'o':
                    handleUsbMediaChange({ type: WPFMessageType.HeadphoneInOut, action: UsbMediaConnectStatus.Disconnected });
                    break;
                case '0':
                case '1':
                case '2':
                case '3':
                case '4':
                case '5':
                case '6':
                case '7':
                case '8':
                case '9': {
                    if (isUseKeypadSiheung) {
                        playTick();
                        window.dispatchEvent(new CustomEvent('keypad-input', { detail: { key: e.key } }));
                        break;
                    }
                    if (isHomePage) break;
                    if (e.key === '0' || e.key === '9') break;

                    if (e.ctrlKey || useModalStackStore.getState().blocksGlobalKeypad()) break;
                    const menuIndex = parseInt(e.key) - 1;
                    const targetItem = gnbItems[menuIndex];
                    if (targetItem?.route) {
                        playTick('ting');
                        if (targetItem.route !== PATHS.HOME.INDEX) {
                            TrackingService.trackMenuByRoute(targetItem.route, targetItem.id ?? undefined);
                        }
                        navigate(targetItem.route);
                        triggerFocusContent();
                    }
                    break;
                }
                case 'F13':
                    handleAnnounce();
                    break;
                case 'F14':
                    handleAnnounceRepeat();
                    break;
                case 'F15':
                    handleAnnounceStop();
                    break;
                case 'Backspace':
                    playTick('ting');
                    handleBackToPrevious();
                    break;
                case 'Home':
                    e.preventDefault();
                    playTick('ting');
                    handleNavigateHome();
                    break;
                case 'AudioVolumeUp': {
                    const state = useConfigStore.getState().config;
                    if (state.tts.speaker === 'headphone') {
                        e.preventDefault();
                        const currentVolume = state.ui.volume || 1;
                        const nextVolume = currentVolume >= 4 ? 1 : currentVolume + 1;
                        playTick('tick');
                        updateConfig({
                            ui: {
                                ...state.ui,
                                volume: nextVolume,
                            },
                        });
                        if (state.tts.voice) {
                            const lang = state.ui.language;
                            if (lang === 'ko') {
                                speak(
                                    `${t('footer.barrier_free.volume_level', { level: convertNumberToKorean(nextVolume) })} ${t('footer.barrier_free.volume')}`,
                                    undefined,
                                    nextVolume
                                );
                            } else {
                                speak(
                                    `${t('footer.barrier_free.volume_level', { level: nextVolume })} ${t('footer.barrier_free.volume')}`,
                                    undefined,
                                    nextVolume
                                );
                            }
                        }
                        handleBarrierFreeUsed();
                    }
                    break;
                }
                case 'AudioVolumeDown': {
                    const state = useConfigStore.getState().config;
                    if (state.tts.speaker === 'headphone') {
                        e.preventDefault();
                        const currentVolume = state.ui.volume || 1;
                        if (currentVolume > 1) {
                            playTick('tick');
                            updateConfig({
                                ui: {
                                    ...state.ui,
                                    volume: 1,
                                },
                            });
                            if (state.tts.voice) {
                                const lang = state.ui.language;
                                if (lang === 'ko') {
                                    speak(
                                        `${t('footer.barrier_free.volume_level', { level: convertNumberToKorean(1) })} ${t('footer.barrier_free.volume')}`,
                                        undefined,
                                        1
                                    );
                                } else {
                                    speak(`${t('footer.barrier_free.volume_level', { level: 1 })} ${t('footer.barrier_free.volume')}`, undefined, 1);
                                }
                            }
                            handleBarrierFreeUsed();
                        }
                    }
                    break;
                }
                case '+': {
                    const state = useConfigStore.getState().config;
                    const currentVolume = state.ui.volume || 1;
                    const nextVolume = currentVolume >= 4 ? 1 : currentVolume + 1;
                    updateConfig({
                        ui: {
                            ...state.ui,
                            volume: nextVolume,
                        },
                    });
                    if (state.tts.voice) {
                        const lang = state.ui.language;
                        if (lang === 'ko') {
                            speak(
                                `${t('footer.barrier_free.volume_level', { level: convertNumberToKorean(nextVolume) })} ${t('footer.barrier_free.volume')}`,
                                undefined,
                                nextVolume
                            );
                        } else {
                            speak(
                                `${t('footer.barrier_free.volume_level', { level: nextVolume })} ${t('footer.barrier_free.volume')}`,
                                undefined,
                                nextVolume
                            );
                        }
                    }
                    handleBarrierFreeUsed();
                    break;
                }
                case '-': {
                    const state = useConfigStore.getState().config;
                    const currentVolume = state.ui.volume || 1;
                    const nextVolume = currentVolume <= 1 ? 4 : currentVolume - 1;
                    updateConfig({
                        ui: {
                            ...state.ui,
                            volume: nextVolume,
                        },
                    });
                    if (state.tts.voice) {
                        const lang = state.ui.language;
                        if (lang === 'ko') {
                            speak(
                                `${t('footer.barrier_free.volume_level', { level: convertNumberToKorean(nextVolume) })} ${t('footer.barrier_free.volume')}`,
                                undefined,
                                nextVolume
                            );
                        } else {
                            speak(
                                `${t('footer.barrier_free.volume_level', { level: nextVolume })} ${t('footer.barrier_free.volume')}`,
                                undefined,
                                nextVolume
                            );
                        }
                    }
                    handleBarrierFreeUsed();
                    break;
                }
                default:
                    break;
            }
        };

        const handleClick = () => {
            if (personDetectedRef.current) {
                hasUsedRef.current = true;
            }
        };

        const handleTouchStart = () => {
            if (personDetectedRef.current) {
                hasUsedRef.current = true;
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        window.addEventListener('click', handleClick, true);
        window.addEventListener('touchstart', handleTouchStart, true);

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('click', handleClick, true);
            window.removeEventListener('touchstart', handleTouchStart, true);
        };
    }, [
        barrierFree,
        handleToggleTTS,
        handleSwitchToHeadphone,
        handleSwitchToExternal,
        handleAnnounce,
        handleAnnounceRepeat,
        handleAnnounceStop,
        handleBackToPrevious,
        handleNavigateHome,
        playTick,
        isScreenSaverFullscreen,
        handleHelpRequest,
        gnbItems,
        navigate,
        triggerFocusContent,
        speakElement,
        tts?.voice,
        handleUsbMediaChange,
        speak,
        t,
        updateConfig,
        handleBarrierFreeUsed,
        isHomePage,
        isHomeMenuVisible,
        setHomeMenuVisible,
        resetHomeMenu,
        screensaverGuideEnabled,
        focusHomeDetailOverlay,
        isUseKeypadSiheung,
    ]);

    const handleUsedTTSModalClose = () => {
        usedTTSModalOpenedByCtrl2Ref.current = false;
        setIsShowUsedTTSModal(false);
        if (tts.speaker !== 'headphone') {
            updateConfig({
                tts: {
                    ...tts,
                    voice: false,
                },
            });
        }
        focusHomeDetailAfterModalIfNeeded();
    };

    const handleUsedTTSModalHomeRedirect = useCallback(() => {
        const keepVoiceOn = usedTTSModalOpenedByCtrl2Ref.current;
        usedTTSModalOpenedByCtrl2Ref.current = false;
        setIsShowUsedTTSModal(false);
        updateConfig({
            tts: {
                ...tts,
                voice: keepVoiceOn ? true : false,
            },
        });
        focusHomeDetailAfterModalIfNeeded();
    }, [tts, updateConfig, focusHomeDetailAfterModalIfNeeded]);

    const handleUsedTTSModalConfirm = () => {
        usedTTSModalOpenedByCtrl2Ref.current = false;
        setIsShowUsedTTSModal(false);
        updateConfig({
            tts: {
                ...tts,
                voice: true,
            },
        });
        focusHomeDetailAfterModalIfNeeded();
    };

    const handleSpeakerModalClose = () => {
        setIsShowHeadPhoneGuide(false);
    };

    const handleCloseHeadPhoneStopModal = () => {
        setIsShowHeadPhoneStop(false);
        clearCountdown();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: true,
            },
        });
    };

    const handleUseSpeakerHeadPhoneStopModal = () => {
        setIsShowHeadPhoneStop(false);
        clearCountdown();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: true,
            },
        });
    };

    const handleTurnOffVoiceHeadPhoneStopModal = useCallback(() => {
        setIsShowHeadPhoneStop(false);
        clearCountdown();
        updateConfig({
            tts: {
                ...tts,
                speaker: 'external',
                voice: false,
            },
        });

        navigate(PATHS.HOME.INDEX);
        resetService.executeReset();
    }, [clearCountdown, navigate, tts, updateConfig]);

    const handleOnConfirmSpeakerCloseModal = useCallback(() => {
        setIsShowSpeakerClose(false);
        updateConfig({ tts: { ...tts, voice: false } });
        navigate(PATHS.HOME.INDEX);
    }, [tts, updateConfig, navigate]);

    const handleBarrierFreeReset = () => {
        setIsBarrierFreeUsed(false);
    };

    const handleFooterCloseModal = useCallback((closeFunction: () => void) => {
        setFooterCloseModalFunction(() => closeFunction);
    }, []);

    // Check if all conditions are back to default values
    const checkDefaultConditions = useCallback(() => {
        const currentLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
        const isDefaultContrast = contrast === false;
        const isDefaultScreenMode = screenMode === 'fullscreen';
        const isDefaultZoomIn = zoomIn === false;
        const isDefaultVolume = volume === CONFIG_CONSTANTS.DEFAULT_VOLUME;
        const isDefaultVoice = tts.voice === false;
        const isDefaultLanguage = language === currentLanguage;

        return isDefaultContrast && isDefaultScreenMode && isDefaultZoomIn && isDefaultVolume && isDefaultVoice && isDefaultLanguage;
    }, [contrast, screenMode, zoomIn, volume, tts.voice, language, storeLangs]);

    useEffect(() => {
        const handler = async (event: MessageEvent) => {
            const message = event.data;

            console.log('Received message from WPF:', message);

            switch (message.type) {
                case WPFMessageType.HeadphoneInOut:
                    handleUsbMediaChange(message);
                    break;
                default:
                    break;
            }
        };

        window.chrome?.webview?.addEventListener('message', handler);
        return () => {
            window.chrome?.webview?.removeEventListener('message', handler);
        };
    }, [handleUsbMediaChange]);

    // Monitor config changes and call reset when all conditions are default
    useEffect(() => {
        if (checkDefaultConditions()) {
            handleBarrierFreeReset();
        }
    }, [checkDefaultConditions]);

    // Cleanup timers on component unmount
    useEffect(() => {
        return () => {
            clearAllTimers();
        };
    }, [clearAllTimers]);

    const zoomScale = zoomLevelToScale(zoomLevel);

    return (
        <>
            <div
                data-zoom-scroll-container
                style={{
                    width: zoomScale > 1 ? `${zoomScale * 100}vw` : '100vw',
                    height: zoomScale > 1 ? `${zoomScale * 100}vh` : '100vh',
                    overflow: zoomScale > 1 ? 'auto' : 'hidden',
                }}
            >
                <div
                    className={clsx(styles['kiosk-layout'], {
                        [styles['kiosk-layout--horizontal']]: isHorizontal,
                        [styles['kiosk-layout--vertical']]: !isHorizontal,
                    })}
                    ref={(element) => {
                        layoutRef.current = element;
                    }}
                    style={{
                        transform: `scale(${zoomScale})`,
                        transformOrigin: 'top left',
                    }}
                >
                    {!isHomePage && !shouldHideCountdownButton() && (
                        <div
                            style={{
                                position: 'absolute',
                                top:
                                    screenMode === 'fullscreen'
                                        ? `${getHSize(12, baseHeight)}`
                                        : isHorizontal
                                          ? `${getHSize(232, baseHeight)}`
                                          : `${getHSize(isEventPage ? 818 : 872, baseHeight)}`,
                                right: isHorizontal ? `${getSize(12, baseWidth)}` : `${getSize(12, baseWidth)}`,
                                zIndex: 50,
                            }}
                        >
                            <CountdownButton onZoomButtonsOpen={handleZoomButtonsOpen} />
                        </div>
                    )}

                    {/* Hidden CountdownButton for contrast mode - runs in background only */}
                    {isBarrierFreeUsed && isHomePage && !shouldHideCountdownButton() && (
                        <div style={{ display: 'none' }}>
                            <CountdownButton
                                onZoomButtonsOpen={handleZoomButtonsOpen}
                                closeModal={() => {
                                    setIsZoomButtonsOpen(false);
                                    handleSpeakerModalClose();
                                    handleUsedTTSModalClose();
                                    setIsShowHeadPhoneStop(false);
                                    setIsShowSpeakerClose(false);
                                    // Also close FooterLayout modals
                                    footerCloseModalFunction?.();
                                }}
                            />
                        </div>
                    )}
                    {!(isScreenSaverFullscreen && isHomePage) && (
                        <div className={clsx(styles['kiosk-layout__gnb-wrapper'], 'focus-border')} data-nav="gnb">
                            <div className={clsx(styles['kiosk-layout__gnb'], 'scrollbar-hide')}>
                                <GNBLayout gnbItems={gnbItemsWithSvg} />
                            </div>
                        </div>
                    )}

                    <div className={clsx(styles['kiosk-layout__content'], 'focus-border')} data-nav="content">
                        <main className={styles['kiosk-layout__content-area']} role="main">
                            {children}
                        </main>
                    </div>

                    {!(isScreenSaverFullscreen && isHomePage) && (
                        <div className={clsx(styles['kiosk-layout__footer'], 'focus-border')} data-nav="footer">
                            <FooterLayout
                                onZoomButtonsOpen={handleZoomButtonsOpen}
                                isZoomButtonsOpen={isZoomButtonsOpen}
                                handleZoomClose={handleZoomClose}
                                onBarrierFreeUsed={handleBarrierFreeUsed}
                                closeModal={handleFooterCloseModal}
                                onBarrierFreeReset={handleBarrierFreeReset}
                            />
                        </div>
                    )}

                    {/* Help Popup - only in screensaver fullscreen mode on home page */}
                    {isScreenSaverFullscreen && <HelpPopup isOpen={isHelpPopupOpen} onClose={handleCloseHelpModal} />}

                    {/* Headphone Guide Modal */}
                    <HeadPhoneGuideModal isOpen={isShowHeadPhoneGuide} onClose={handleSpeakerModalClose} language={languageCheck} />

                    {/* Used TTS Modal */}
                    <UsedTTSModal
                        isOpen={isShowUsedTTSModal}
                        onHomeRedirect={handleUsedTTSModalHomeRedirect}
                        onClose={handleUsedTTSModalClose}
                        onConfirm={handleUsedTTSModalConfirm}
                    />

                    {/* Headphone Stop Modal */}
                    <HeadPhoneStopModal
                        remaining={remaining}
                        isOpen={isShowHeadPhoneStop}
                        onClose={handleCloseHeadPhoneStopModal}
                        onUseSpeaker={handleUseSpeakerHeadPhoneStopModal}
                        onTurnOffVoice={handleTurnOffVoiceHeadPhoneStopModal}
                    />
                    {/* Speaker Close Modal */}
                    <SpeakerCloseModal isOpen={isShowSpeakerClose} onClose={() => setIsShowSpeakerClose(false)} onConfirm={handleOnConfirmSpeakerCloseModal} />

                    <ChangedToHeadphoneModal
                        isOpen={isShowChangedToHeadphoneModal}
                        onClose={() => {
                            setIsShowChangedToHeadphoneModal(false);
                            focusHomeDetailAfterModalIfNeeded();
                        }}
                        onConfirm={() => {
                            navigate(PATHS.HOME.INDEX);
                            setIsShowChangedToHeadphoneModal(false);
                            focusHomeDetailAfterModalIfNeeded();
                        }}
                    />

                    {/* Portal container for TimeExtensionModal - must be inside scaled div but outside stacking context */}
                    <div id="time-extension-modal-root" style={{ position: 'relative', zIndex: 99999 }} />
                </div>
            </div>
            <ZoomButtons isOpen={isZoomButtonsOpen} onClose={handleZoomClose} onZoom={handleZoom} isZoomedIn={zoomIn} />
        </>
    );
};
